import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Select, Spin } from 'antd';
import CorporateGroupModalWrapper from './CorporateGroupModal.styles';
import { corporateGroupService } from '@iso/services';

const { TextArea } = Input;

const CorporateGroupModal = (props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState(null);
  const isEdit = Boolean(props.corporateGroupData && props.corporateGroupData.id);

  useEffect(() => {
    if (props.visible) {
      form.setFieldsValue({
        name: props.corporateGroupData?.name || '',
        description: props.corporateGroupData?.description || '',
        pin: props.corporateGroupData?.pin || '',
        lora_organisation_ids: (props.corporateGroupData?.lora_organisations || []).map(
          (o) => o.lora_organisation_id
        ),
      });
      setOrgsLoading(true);
      setOrgsError(null);
      corporateGroupService
        .getLoraOrganisations()
        .then((res) => {
          if (res && res.code === '0000' && Array.isArray(res.data)) {
            setOrgOptions(res.data);
          } else {
            setOrgOptions([]);
            setOrgsError(
              (res && res.message) ||
                'Could not load LoRa organisations. Make sure you are signed in as a system admin and the LoRa connection is available.'
            );
          }
        })
        .catch(() => setOrgsError('Could not load LoRa organisations.'))
        .finally(() => setOrgsLoading(false));
    }
  }, [props.visible, props.corporateGroupData, form]);

  const cancelModal = () => {
    props.onCancel();
    form.resetFields();
    setLoading(false);
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setLoading(true);
        const selectedIds = values.lora_organisation_ids || [];
        const data = {
          name: values.name,
          description: values.description || '',
          pin: values.pin,
          lora_organisations: selectedIds.map((id) => ({
            id,
            name: (orgOptions.find((o) => o.id === id) || {}).name || null,
          })),
        };
        corporateGroupService
          .storeOrUpdate(data, props.corporateGroupData?.id)
          .then((res) => {
            if (res && res.code === '0000') {
              message.success(isEdit ? 'Corporate group updated' : 'Corporate group created');
              props.onSuccess();
              cancelModal();
            } else {
              message.error(res?.message || 'Failed to save corporate group');
              setLoading(false);
            }
          })
          .catch(() => {
            message.error('Failed to save corporate group');
            setLoading(false);
          });
      })
      .catch(() => {
        // validation error — Antd shows the field message
      });
  };

  return (
    <CorporateGroupModalWrapper
      visible={props.visible}
      onCancel={cancelModal}
      maskClosable={false}
      title={isEdit ? 'Edit Corporate Group' : 'Add Corporate Group'}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          Cancel
        </Button>,
        <Button
          key="submit"
          className="btn-success"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" scrollToFirstError>
        <div className="form-body">
          <Form.Item
            name="name"
            label="Corporate Group Name"
            rules={[
              { required: true, whitespace: true, message: 'Name is required' },
              { max: 255, message: 'Name is too long' },
            ]}
          >
            <Input size="large" placeholder="e.g. Harvest Group" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Optional description" />
          </Form.Item>

          <Form.Item
            name="pin"
            label="4-digit PIN"
            rules={[
              { required: true, message: 'PIN is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!/^\d{4}$/.test(value)) {
                    return Promise.reject(new Error('PIN must be exactly 4 digits'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              size="large"
              maxLength={4}
              inputMode="numeric"
              placeholder="0000"
              autoComplete="new-password"
            />
          </Form.Item>
          <div className="pin-hint">
            Users assigning a property to this corporate group will be prompted for this PIN.
          </div>

          <Form.Item
            name="lora_organisation_ids"
            label="Linked LoRa organisations"
            style={{ marginTop: 12 }}
            validateStatus={orgsError ? 'error' : undefined}
            help={orgsError || undefined}
          >
            <Select
              mode="multiple"
              allowClear
              size="large"
              placeholder="Select the client's LoRa organisation(s)"
              optionFilterProp="children"
              showSearch
              loading={orgsLoading}
              notFoundContent={
                orgsLoading ? <Spin size="small" /> : orgsError ? 'Failed to load' : 'No organisations found'
              }
            >
              {orgOptions.map((o) => (
                <Select.Option key={o.id} value={o.id}>
                  {o.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className="pin-hint">
            Property owners/admins in this group can add devices from the linked
            organisation(s) onto their own properties.
          </div>
        </div>
      </Form>
    </CorporateGroupModalWrapper>
  );
};

export default CorporateGroupModal;
